/**
 * Browser-Betrieb eines „Web-Intercom"-Geräts (bidirektional) – ohne Desktop.
 *
 * Verhält sich wie ein ext-Gerät der Desktop-App:
 *  - SENDEN: publiziert `ext-mic:<deviceId>` (Mikrofon) → andere Panels hören es
 *    (Listen-Port / external-device Listen).
 *  - EMPFANGEN: abonniert genau die Tracks, die an seinen `ext-spk` adressiert
 *    sind (panel-mic:<panel>:<button> mit targets.deviceId == diese deviceId)
 *    sowie optional die konfigurierte PGM-Quelle, und spielt sie ab.
 *
 * Gepaart wird es im Admin wie ein normales Gerät (mic+spk sind automatisch
 * abgeleitet), Panels sprechen/hören es über „external-device"-Buttons.
 */
import {
  Room,
  RoomEvent,
  Track,
  type LocalAudioTrack,
  type RemoteAudioTrack,
  type RemoteTrackPublication,
  type RemoteParticipant,
} from 'livekit-client';
import { makePortKey, parsePortKey } from '@livepuls/types';
import { api } from './api.js';

/** Öffentliche Mindest-Info eines Geräts (GET /external-devices/:id/public). */
interface DevicePublic {
  id: string;
  name: string;
  web?: boolean;
  pgmPortKey?: string;
  pgmVolume?: number;
}

export const TEST_TONE_DEVICE_ID = '__livepuls_testtone__';

interface TokenResponse {
  url: string;
  token: string;
  iceServers?: Array<{ urls: string | string[]; username?: string; credential?: string }>;
}

function safeJson<T>(s?: string | null): T | null {
  if (!s) return null;
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}

interface TargetsMeta {
  targets?: Array<{ deviceId?: string; spkPortKey?: string; buttonId?: string }>;
}

export class IntercomDeviceEngine {
  room: Room | null = null;
  level = 0;
  error: string | null = null;
  onChange: () => void = () => {};

  device: DevicePublic | null = null;
  private deviceId = '';
  private identity = '';
  private gain = 1;
  private micDeviceId = '';

  private ctx: AudioContext | null = null;
  private rawStream: MediaStream | null = null;
  private testOsc: OscillatorNode | null = null;
  private gainNode: GainNode | null = null;
  private analyser: AnalyserNode | null = null;
  private dest: MediaStreamAudioDestinationNode | null = null;
  private localTrack: LocalAudioTrack | null = null;
  private raf: number | null = null;

  /** Wiedergabe-Elemente eingehender Tracks per track.sid. */
  private playing = new Map<string, HTMLAudioElement>();

  get connected(): boolean {
    return this.room?.state === 'connected';
  }

  async connect(deviceId: string, micDeviceId: string, gain: number): Promise<void> {
    this.deviceId = deviceId;
    this.micDeviceId = micDeviceId;
    this.gain = gain;
    this.identity = `extdev-${deviceId}`;
    this.device = await api
      .get<DevicePublic>(`/api/v1/external-devices/${deviceId}/public`)
      .catch(() => null);

    await this.startCapture();

    const tok = await api.post<TokenResponse>('/api/v1/livekit/token', {
      identity: this.identity,
      role: 'publisher-subscriber',
    });
    const iceServers: RTCIceServer[] = (tok.iceServers ?? []).map((s) => ({
      urls: s.urls,
      ...(s.username !== undefined ? { username: s.username } : {}),
      ...(s.credential !== undefined ? { credential: s.credential } : {}),
    }));
    const room = new Room({ adaptiveStream: false, dynacast: false });
    room.on(RoomEvent.TrackSubscribed, (t, _pub, _p) => {
      if (t.kind === Track.Kind.Audio) this.play(t as RemoteAudioTrack, _pub);
    });
    room.on(RoomEvent.TrackUnsubscribed, (t) => this.removePlayed(t.sid ?? ''));
    room.on(RoomEvent.ParticipantMetadataChanged, () => this.recompute());
    room.on(RoomEvent.TrackPublished, () => this.recompute());
    room.on(RoomEvent.ParticipantConnected, () => this.recompute());
    room.on(RoomEvent.Disconnected, () => this.onChange());
    room.on(RoomEvent.Connected, () => this.onChange());
    await room.connect(tok.url, tok.token, {
      autoSubscribe: false,
      ...(iceServers.length > 0 ? { rtcConfig: { iceServers } } : {}),
    });
    this.room = room;
    // Metadata: registriert ext-mic UND ext-spk als Ports (Backend-Reconcile).
    try {
      const label = this.device?.name ?? 'Web-Intercom';
      await room.localParticipant.setMetadata(
        JSON.stringify({
          slots: [
            { slotId: deviceId, kind: 'ext-mic', label },
            { slotId: deviceId, kind: 'ext-spk', label },
          ],
        }),
      );
    } catch {
      /* nice-to-have */
    }
    await this.publishMic();
    this.recompute();
    this.onChange();
  }

  /** Entscheidet, ob eine Remote-Audio-Publication für dieses Gerät bestimmt ist. */
  private wantPlay(pub: RemoteTrackPublication, p: RemoteParticipant): boolean {
    const name = pub.trackName ?? '';
    if (name.startsWith('panel-mic:')) {
      const parts = name.split(':');
      if (parts.length < 3) return false; // geteilter Mic → nicht für uns
      const buttonId = parts[2];
      const meta = safeJson<TargetsMeta>(p.metadata);
      return (meta?.targets ?? []).some(
        (t) =>
          t.buttonId === buttonId &&
          (t.deviceId === this.deviceId ||
            (!!t.spkPortKey && parsePortKey(t.spkPortKey)?.identity === this.identity)),
      );
    }
    if (name.startsWith('pgm:') && this.device?.pgmPortKey) {
      const key = makePortKey(p.identity, 'pgm', name.slice('pgm:'.length));
      return key === this.device.pgmPortKey;
    }
    return false;
  }

  /** Abonniert genau die für uns bestimmten Tracks (sonst Bandbreite sparen). */
  private recompute(): void {
    if (!this.room) return;
    for (const p of this.room.remoteParticipants.values()) {
      for (const pub of p.trackPublications.values()) {
        if (pub.kind !== Track.Kind.Audio) continue;
        pub.setSubscribed(this.wantPlay(pub, p));
      }
    }
  }

  private play(track: RemoteAudioTrack, pub: RemoteTrackPublication): void {
    const sid = track.sid ?? `t-${Math.random()}`;
    if (this.playing.has(sid)) return;
    const el = document.createElement('audio');
    el.autoplay = true;
    (el as HTMLAudioElement & { playsInline: boolean }).playsInline = true;
    el.setAttribute('playsinline', '');
    el.style.display = 'none';
    document.body.appendChild(el);
    track.attach(el);
    // PGM bekommt die im Gerät konfigurierte Lautstärke.
    if ((pub.trackName ?? '').startsWith('pgm:')) {
      el.volume = Math.max(0, Math.min(1, (this.device?.pgmVolume ?? 100) / 100));
    }
    void el.play().catch(() => {});
    this.playing.set(sid, el);
    this.onChange();
  }

  private removePlayed(sid: string): void {
    const el = this.playing.get(sid);
    if (el) {
      el.srcObject = null;
      el.remove();
      this.playing.delete(sid);
      this.onChange();
    }
  }

  /** Anzahl aktuell empfangener (gespielter) Strecken – für die UI. */
  get incomingCount(): number {
    return this.playing.size;
  }

  unlock(): void {
    if (this.ctx?.state === 'suspended') void this.ctx.resume().catch(() => {});
    for (const el of this.playing.values()) void el.play().catch(() => {});
  }

  async setDevice(micDeviceId: string): Promise<void> {
    this.micDeviceId = micDeviceId;
    const wasConnected = this.connected;
    await this.unpublishMic();
    await this.releaseCapture();
    await this.startCapture();
    if (wasConnected) await this.publishMic();
    this.onChange();
  }

  setGain(gain: number): void {
    this.gain = gain;
    if (this.gainNode) this.gainNode.gain.value = gain;
  }

  async disconnect(): Promise<void> {
    await this.unpublishMic();
    await this.releaseCapture();
    for (const el of this.playing.values()) {
      el.srcObject = null;
      el.remove();
    }
    this.playing.clear();
    if (this.room) {
      await this.room.disconnect();
      this.room = null;
    }
    this.onChange();
  }

  private async startCapture(): Promise<void> {
    if (this.dest && (this.rawStream || this.testOsc)) return;
    const ctx = this.ctx ?? new AudioContext({ sampleRate: 48000 });
    this.ctx = ctx;
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch {
        /* */
      }
    }
    const gain = ctx.createGain();
    gain.gain.value = this.gain;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    const dest = ctx.createMediaStreamDestination();
    if (this.micDeviceId === TEST_TONE_DEVICE_ID) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = 1000;
      const tg = ctx.createGain();
      tg.gain.value = 0.15;
      osc.connect(tg);
      tg.connect(gain);
      osc.start();
      this.testOsc = osc;
    } else {
      const constraints: MediaTrackConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: false,
        ...(this.micDeviceId ? { deviceId: { exact: this.micDeviceId } } : {}),
      };
      const stream = await navigator.mediaDevices.getUserMedia({ audio: constraints });
      this.rawStream = stream;
      ctx.createMediaStreamSource(stream).connect(gain);
    }
    gain.connect(analyser);
    gain.connect(dest);
    this.gainNode = gain;
    this.analyser = analyser;
    this.dest = dest;
    this.error = null;
    this.startMeter();
  }

  private async publishMic(): Promise<void> {
    if (!this.room || this.room.state !== 'connected') return;
    if (this.localTrack) return;
    if (!this.dest) await this.startCapture();
    const track = this.dest?.stream.getAudioTracks()[0];
    if (!track) throw new Error('Capture lieferte keinen Track');
    const pub = await this.room.localParticipant.publishTrack(track, {
      name: `ext-mic:${this.deviceId}`,
      dtx: false,
      red: false,
    });
    this.localTrack = (pub.track as LocalAudioTrack | undefined) ?? null;
  }

  private async unpublishMic(): Promise<void> {
    if (this.localTrack && this.room?.state === 'connected') {
      await this.room.localParticipant
        .unpublishTrack(this.localTrack.mediaStreamTrack)
        .catch(() => {});
    }
    this.localTrack = null;
  }

  private async releaseCapture(): Promise<void> {
    if (this.testOsc) {
      try {
        this.testOsc.stop();
        this.testOsc.disconnect();
      } catch {
        /* */
      }
      this.testOsc = null;
    }
    if (this.rawStream) {
      for (const t of this.rawStream.getTracks()) {
        try {
          t.stop();
        } catch {
          /* */
        }
      }
      this.rawStream = null;
    }
    try {
      this.gainNode?.disconnect();
      this.analyser?.disconnect();
      this.dest?.disconnect();
    } catch {
      /* */
    }
    this.gainNode = null;
    this.analyser = null;
    this.dest = null;
    if (this.raf !== null) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
    this.level = 0;
  }

  private startMeter(): void {
    if (!this.analyser) return;
    const buf = new Float32Array(this.analyser.fftSize);
    const tick = (): void => {
      if (!this.analyser) return;
      this.analyser.getFloatTimeDomainData(buf);
      let peak = 0;
      for (const v of buf) {
        const a = Math.abs(v);
        if (a > peak) peak = a;
      }
      this.level = peak;
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }
}
